import { useEffect, useRef } from 'react'
import { useTaskManager } from '@/contexts/TaskManagerContext'

const BASE_TITLE = 'MerchMuse - AI-Powered Listing Generator for Print-on-Demand'

/**
 * Hook to update document title with unread completion count.
 *
 * When the tab is unfocused and tasks complete:
 * - Title shows "(3) MerchMuse" format
 * - Count resets when tab regains focus
 *
 * Usage: Call once in App.tsx or a root component.
 */
export function useDocumentTitle(): void {
  const { unseenCount } = useTaskManager()
  const wasHiddenRef = useRef(false)
  const pendingCountRef = useRef(0)

  // Track unseen completions while tab is hidden
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab became hidden - start tracking
        wasHiddenRef.current = true
        pendingCountRef.current = 0
      } else if (wasHiddenRef.current) {
        // Tab became visible again - clear the count display
        wasHiddenRef.current = false
        pendingCountRef.current = 0
        document.title = BASE_TITLE
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  // Update title when unseen count changes
  useEffect(() => {
    if (document.hidden && unseenCount > 0) {
      // Tab is hidden and we have unseen completions
      document.title = `(${unseenCount}) ${BASE_TITLE}`
    } else if (!document.hidden) {
      // Tab is visible - show normal title
      document.title = BASE_TITLE
    }
  }, [unseenCount])

  // Reset title on unmount
  useEffect(() => {
    return () => {
      document.title = BASE_TITLE
    }
  }, [])
}
